import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import {
  ChevronsLeft,
  ChevronsRight,
  Crosshair,
  Expand,
  Film,
  Globe,
  MousePointer2,
  Pause,
  Play,
  RotateCcw,
  SkipBack,
  SkipForward,
  Terminal,
  Zap,
  ZoomIn,
  ZoomOut,
} from 'lucide-react';
import type { DetrixSession } from '../../types';
import { usePlaybackStore } from '../../store/usePlaybackStore';
import { useSessionUiStore } from '../../store/useSessionUiStore';
import { formatDuration, formatSessionOffset } from '../../utils/time';
import { sessionTimeBounds } from '../../core/session';
import { findFirstErrorInSession, getErrorCorrelation, isSelectionError } from '../../utils/sessionErrorSignals';
import {
  buildRulerTicks,
  buildSnapTimes,
  buildTimelineHoverSamples,
  nearestHoverSample,
  nearestSnapTime,
  panViewByMs,
  pctInView,
  timeFromFrac,
  zoomAroundAnchor,
} from '../../utils/timelineViewport';
import { filterUnifiedNetworkRows, mergeUnifiedNetworkRows } from '../../utils/networkRowFilter';
import {
  layoutForHeight,
  TIMELINE_EDGE_INSET_PX,
  timelineAxisMetrics,
  timelineMarkerLeftStyle,
} from './timelineLayout';
import { buildLandmarks, isEventError, isNetworkError, shortUrl } from './timelineModel';
import {
  cn,
  HoverTipOverlay,
  isErrorOrWarnTone,
  type HoverTipState,
  TimelineDot,
  TrackPlayheadLine,
  type DotTone,
} from './timelineUi';

interface SessionTimelineProps {
  session: DetrixSession;
}

export const SessionTimeline: React.FC<SessionTimelineProps> = ({ session }) => {
  const {
    steps = [],
    actions = [],
    network = [],
    events = [],
    browserResources = [],
  } = session;

  const {
    playbackBounds,
    playheadMs,
    isPlaying,
    playbackRate,
    setPlayheadMs,
    togglePlayback,
    resetPlayback,
    stepPlaybackLandmarks,
    setPlaybackRate,
  } = usePlaybackStore();

  const {
    setActiveTab,
    setSelectedId,
    selectedId,
    activeTab,
    focusErrorsOnly,
    setFocusErrorsOnly,
    networkUrlFilter,
    networkTypeFilters,
  } = useSessionUiStore();

  const rootRef = useRef<HTMLDivElement>(null);
  const scrubRef = useRef<HTMLDivElement>(null);
  const trackScrollRef = useRef<HTMLDivElement>(null);
  const zoomSurfaceRef = useRef<HTMLDivElement>(null);
  const scrubWidthRef = useRef(1);
  const lastWheelAnchorRef = useRef<number | null>(null);
  const surfaceDragRef = useRef<{
    mode: 'none' | 'click-pending' | 'pan';
    pointerId: number;
    x0: number;
    lastX: number;
    axisEl: HTMLElement | null;
  }>({ mode: 'none', pointerId: -1, x0: 0, lastX: 0, axisEl: null });

  const [frameHeight, setFrameHeight] = useState(96);
  const [hoverTip, setHoverTip] = useState<HoverTipState>(null);
  const [viewWindow, setViewWindow] = useState<{ start: number; end: number } | null>(null);
  const [followPlayhead, setFollowPlayhead] = useState(false);
  const [playheadDragging, setPlayheadDragging] = useState(false);

  useEffect(() => {
    const el = rootRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver((entries) => {
      const e = entries[0];
      if (e) setFrameHeight(Math.round(e.contentRect.height));
    });
    ro.observe(el);
    setFrameHeight(Math.round(el.getBoundingClientRect().height));
    return () => ro.disconnect();
  }, []);

  const layout = layoutForHeight(frameHeight);

  const { min: tMin, max: tMax, duration } = useMemo(() => sessionTimeBounds(session), [session]);

  const viewStart = viewWindow?.start ?? tMin;
  const viewEnd = viewWindow?.end ?? tMax;

  useLayoutEffect(() => {
    setViewWindow({ start: tMin, end: tMax });
  }, [session.sessionId, tMin, tMax]);

  useEffect(() => {
    const el = trackScrollRef.current;
    if (!el) return;
    const clearTip = () => setHoverTip(null);
    el.addEventListener('scroll', clearTip, { passive: true });
    return () => el.removeEventListener('scroll', clearTip);
  }, []);

  const landmarks = useMemo(() => buildLandmarks(session, tMin, tMax), [session, tMin, tMax]);

  const firstError = useMemo(() => findFirstErrorInSession(session), [session]);

  const correlation = useMemo(() => {
    if (!selectedId) return null;
    if (!isSelectionError(session, activeTab, selectedId)) return null;
    return getErrorCorrelation(session, activeTab, selectedId);
  }, [session, activeTab, selectedId]);

  const errorAutoFocusRef = useRef<string | null>(null);
  useLayoutEffect(() => {
    if (!firstError) {
      errorAutoFocusRef.current = null;
      return;
    }
    if (errorAutoFocusRef.current === session.sessionId) return;
    errorAutoFocusRef.current = session.sessionId;
    setPlayheadMs(firstError.time, { pause: true });
    queueMicrotask(() => {
      const row = rootRef.current?.querySelector(`[data-timeline-track="${firstError.tab}"]`);
      row?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    });
  }, [session.sessionId, firstError, setPlayheadMs]);

  const snapTimes = useMemo(() => buildSnapTimes(session, tMin, tMax), [session, tMin, tMax]);
  const filteredNetworkLaneRows = useMemo(() => {
    const merged = mergeUnifiedNetworkRows(network, browserResources);
    return filterUnifiedNetworkRows(merged, networkUrlFilter, networkTypeFilters);
  }, [network, browserResources, networkUrlFilter, networkTypeFilters]);
  const hoverSamples = useMemo(
    () => buildTimelineHoverSamples(session, tMin, tMax, networkUrlFilter, networkTypeFilters),
    [session, tMin, tMax, networkUrlFilter, networkTypeFilters],
  );
  const tickTarget = layout === 'expanded' ? 10 : layout === 'standard' ? 7 : 5;
  const ticks = useMemo(
    () => buildRulerTicks(viewStart, viewEnd, tickTarget),
    [viewStart, viewEnd, tickTarget],
  );

  useLayoutEffect(() => {
    const el = scrubRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(() => {
      scrubWidthRef.current = timelineAxisMetrics(el).innerW;
    });
    ro.observe(el);
    scrubWidthRef.current = timelineAxisMetrics(el).innerW;
    return () => ro.disconnect();
  }, []);

  const synced =
    playbackBounds?.sessionId === session.sessionId && playbackBounds.min === tMin && playbackBounds.max === tMax;
  const effectivePlayhead = synced ? playheadMs : tMin;
  const viewSpan = Math.max(1e-9, viewEnd - viewStart);
  const playheadPctRaw = pctInView(viewStart, viewEnd, effectivePlayhead);
  const playheadPct = Math.min(100, Math.max(0, playheadPctRaw));

  const scrubToClientX = useCallback(
    (clientX: number, opts?: { snap?: boolean; axisEl?: HTMLElement | null }) => {
      const el = opts?.axisEl ?? scrubRef.current;
      if (!el) return;
      const { innerLeft, innerW } = timelineAxisMetrics(el);
      const x = Math.min(1, Math.max(0, (clientX - innerLeft) / innerW));
      let ts = timeFromFrac(viewStart, viewEnd, x);
      ts = Math.min(tMax, Math.max(tMin, ts));
      if (opts?.snap) {
        const thr = (20 / innerW) * (viewEnd - viewStart);
        ts = nearestSnapTime(snapTimes, ts, thr) ?? ts;
      }
      setPlayheadMs(ts, { pause: true });
    },
    [viewStart, viewEnd, tMin, tMax, snapTimes, setPlayheadMs],
  );

  const applyZoom = useCallback(
    (factor: number, anchorT: number) => {
      setViewWindow((prev) => {
        const vs = prev?.start ?? tMin;
        const ve = prev?.end ?? tMax;
        const z = zoomAroundAnchor(tMin, tMax, vs, ve, factor, anchorT);
        return { start: z.start, end: z.end };
      });
    },
    [tMin, tMax],
  );

  const zoomInAt = useCallback(
    (anchorT: number) => {
      applyZoom(0.82, anchorT);
    },
    [applyZoom],
  );

  const zoomOutAt = useCallback(
    (anchorT: number) => {
      applyZoom(1 / 0.82, anchorT);
    },
    [applyZoom],
  );

  useLayoutEffect(() => {
    if (!followPlayhead || !synced) return;
    if (viewEnd - viewStart >= duration - 1e-6) return;
    const px = (effectivePlayhead - viewStart) / viewSpan;
    if (Math.abs(px - 0.5) < 0.015) return;
    const deltaMs = (px - 0.5) * viewSpan;
    setViewWindow((prev) => {
      const vs = prev?.start ?? tMin;
      const ve = prev?.end ?? tMax;
      const p = panViewByMs(tMin, tMax, vs, ve, deltaMs);
      return { start: p.start, end: p.end };
    });
  }, [effectivePlayhead, followPlayhead, synced, duration, tMin, tMax, viewEnd, viewStart, viewSpan]);

  useEffect(() => {
    const el = zoomSurfaceRef.current;
    if (!el) return;
    const onWheel = (e: WheelEvent) => {
      if (e.ctrlKey || e.metaKey) {
        e.preventDefault();
        const scrub = scrubRef.current;
        if (!scrub) return;
        const { innerLeft, innerW } = timelineAxisMetrics(scrub);
        const frac = Math.min(1, Math.max(0, (e.clientX - innerLeft) / innerW));
        setViewWindow((prev) => {
          const vs = prev?.start ?? tMin;
          const ve = prev?.end ?? tMax;
          const anchorT = timeFromFrac(vs, ve, frac);
          const factor = e.deltaY > 0 ? 1.09 : 1 / 1.09;
          const z = zoomAroundAnchor(tMin, tMax, vs, ve, factor, anchorT);
          return { start: z.start, end: z.end };
        });
        return;
      }

      const horizontalDominant =
        Math.abs(e.deltaX) > 1 && Math.abs(e.deltaX) > Math.abs(e.deltaY) * 0.85;
      const shiftPan = e.shiftKey && Math.abs(e.deltaY) > 0.5;
      if (!horizontalDominant && !shiftPan) return;

      e.preventDefault();
      const scrub = scrubRef.current;
      const innerW = scrub ? timelineAxisMetrics(scrub).innerW : Math.max(1, scrubWidthRef.current);
      const panPx = horizontalDominant ? e.deltaX : e.deltaY;
      setViewWindow((prev) => {
        const vs = prev?.start ?? tMin;
        const ve = prev?.end ?? tMax;
        const span = Math.max(1e-9, ve - vs);
        const deltaMs = (-panPx / innerW) * span;
        return panViewByMs(tMin, tMax, vs, ve, deltaMs);
      });
    };
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, [tMin, tMax]);

  useEffect(() => {
    if (!playheadDragging) return;
    const onMove = (e: PointerEvent) => {
      scrubToClientX(e.clientX, { axisEl: scrubRef.current });
    };
    const onUp = (e: PointerEvent) => {
      scrubToClientX(e.clientX, { snap: true, axisEl: scrubRef.current });
      setPlayheadDragging(false);
    };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    window.addEventListener('pointercancel', onUp);
    return () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      window.removeEventListener('pointercancel', onUp);
    };
  }, [playheadDragging, scrubToClientX]);

  const anchorForZoomUi = useCallback(() => {
    const a = lastWheelAnchorRef.current;
    if (a !== null && Number.isFinite(a)) return Math.min(tMax, Math.max(tMin, a));
    return Math.min(tMax, Math.max(tMin, effectivePlayhead));
  }, [effectivePlayhead, tMin, tMax]);

  const onTimelineSurfacePointerDown = (e: React.PointerEvent) => {
    if (e.button !== 0) return;
    const t = e.target as HTMLElement;
    if (t.closest('button')) return;
    if (t.closest('[data-playhead-handle]')) return;
    const lane = e.currentTarget as HTMLElement;
    const { innerLeft, innerW } = timelineAxisMetrics(lane);
    lastWheelAnchorRef.current = timeFromFrac(
      viewStart,
      viewEnd,
      Math.min(1, Math.max(0, (e.clientX - innerLeft) / innerW)),
    );
    surfaceDragRef.current = {
      mode: 'click-pending',
      pointerId: e.pointerId,
      x0: e.clientX,
      lastX: e.clientX,
      axisEl: lane,
    };
    e.currentTarget.setPointerCapture(e.pointerId);
  };

  const onTimelineSurfacePointerMove = (e: React.PointerEvent) => {
    const d = surfaceDragRef.current;
    if (d.mode === 'none' || d.pointerId !== e.pointerId) return;
    const lane = d.axisEl ?? (e.currentTarget as HTMLElement);
    const { innerW } = timelineAxisMetrics(lane);
    if (d.mode === 'click-pending' && Math.abs(e.clientX - d.x0) > 4) {
      d.mode = 'pan';
    }
    if (d.mode === 'pan') {
      const deltaPx = e.clientX - d.lastX;
      d.lastX = e.clientX;
      setViewWindow((prev) => {
        const vs = prev?.start ?? tMin;
        const ve = prev?.end ?? tMax;
        const span = Math.max(1e-9, ve - vs);
        const deltaMs = (-deltaPx / innerW) * span;
        return panViewByMs(tMin, tMax, vs, ve, deltaMs);
      });
    }
  };

  const onTimelineSurfacePointerUp = (e: React.PointerEvent) => {
    const d = surfaceDragRef.current;
    if (d.pointerId !== e.pointerId) return;
    const mode = d.mode;
    try {
      e.currentTarget.releasePointerCapture(e.pointerId);
    } catch {
      /* ignore */
    }
    if (mode === 'click-pending') {
      scrubToClientX(d.x0, { snap: true, axisEl: d.axisEl });
    }
    d.mode = 'none';
    d.pointerId = -1;
    d.axisEl = null;
  };

  const rulerHoverRaf = useRef<number | null>(null);
  const onRulerPointerMove = (e: React.PointerEvent) => {
    if (surfaceDragRef.current.mode === 'pan') return;
    if (rulerHoverRaf.current !== null) return;
    rulerHoverRaf.current = window.requestAnimationFrame(() => {
      rulerHoverRaf.current = null;
      const scrub = scrubRef.current;
      if (!scrub) return;
      const { innerLeft, innerW } = timelineAxisMetrics(scrub);
      const frac = Math.min(1, Math.max(0, (e.clientX - innerLeft) / innerW));
      const tHover = timeFromFrac(viewStart, viewEnd, frac);
      lastWheelAnchorRef.current = tHover;
      const sample = nearestHoverSample(hoverSamples, tHover);
      if (!sample) {
        setHoverTip(null);
        return;
      }
      const thr = Math.max(viewSpan * 0.004, 12);
      if (Math.abs(sample.t - tHover) > thr) {
        setHoverTip(null);
        return;
      }
      setHoverTip({
        x: e.clientX,
        y: e.clientY,
        lines: sample.lines.filter(Boolean),
        placeAbove: true,
      });
    });
  };

  const onRulerPointerLeave = () => {
    if (rulerHoverRaf.current !== null) {
      cancelAnimationFrame(rulerHoverRaf.current);
      rulerHoverRaf.current = null;
    }
    setHoverTip(null);
  };

  const onScrubPointerMove = (e: React.PointerEvent) => {
    onTimelineSurfacePointerMove(e);
    if (surfaceDragRef.current.mode !== 'pan') onRulerPointerMove(e);
  };

  const rowH = layout === 'compact' ? 'h-8' : layout === 'standard' ? 'h-9' : 'h-10';
  const labelSize = layout === 'compact' ? 'text-[8px]' : 'text-[9px]';

  return (
    <div
      ref={rootRef}
      className="flex min-h-0 w-full min-w-0 shrink-0 flex-col overflow-hidden bg-[var(--tl-canvas)] text-[var(--tl-root-text)] shadow-[inset_0_0_0_1px_var(--timeline-chrome-outline)]"
    >
      <HoverTipOverlay tip={hoverTip} />
      {/* Time strip — large playhead readout (video / NLE style) */}
      <div className="shrink-0 rounded-t-lg border-b border-[var(--tl-border)] bg-gradient-to-b from-[var(--tl-strip-from)] via-[var(--tl-strip-via)] to-[var(--tl-strip-to)] px-2.5 pb-1.5 pt-1 shadow-[inset_0_-1px_0_var(--tl-inset-sky)] sm:px-3 sm:pb-1.5 sm:pt-1">
        <div className="flex min-w-0 items-end justify-between gap-2 sm:items-center">
          <div className="flex min-w-0 items-baseline gap-2 sm:items-center sm:gap-3">
            <span className="hidden shrink-0 text-[8px] font-semibold uppercase tracking-[0.18em] text-[var(--tl-label)] sm:inline">Playhead</span>
            <div
              className={cn(
                'min-w-0 font-mono text-[clamp(0.95rem,2.1vw,1.2rem)] font-semibold tabular-nums leading-none tracking-tight text-[var(--tl-strong)]',
                playheadDragging ? 'text-sky-200 drop-shadow-[0_0_10px_rgba(56,189,248,0.35)]' : '',
              )}
            >
              {formatSessionOffset(effectivePlayhead)}
            </div>
          </div>
          <div className="flex shrink-0 items-center gap-2 sm:gap-2.5">
            <div className="hidden h-4 w-px bg-[var(--tl-divider-v)] sm:block" aria-hidden />
            <div className="flex items-center gap-1.5 text-[8px] font-bold uppercase tracking-[0.16em] text-[var(--tl-muted)]">
              <Film className="h-3 w-3 text-sky-400/90" aria-hidden />
              <span className="hidden sm:inline">Timeline</span>
            </div>
            <span className="font-mono text-[9px] tabular-nums text-[var(--tl-muted)] sm:text-[10px]">{formatDuration(duration)}</span>
            {events.filter(isEventError).length > 0 ? (
              <div className="rounded border border-rose-500/35 bg-rose-950/45 px-1.5 py-px font-mono text-[9px] font-medium text-rose-100/95">
                {events.filter(isEventError).length} err
              </div>
            ) : (
              <span className="text-[9px] font-medium text-emerald-400/75">0 err</span>
            )}
          </div>
        </div>
        <div className="mt-1 flex min-w-0 items-center justify-between gap-2 border-t border-[var(--tl-border-subtle)] pt-1 font-mono text-[8px] tabular-nums text-[var(--tl-faint)] sm:text-[9px]">
          <span className="min-w-0 truncate">
            {viewEnd - viewStart < duration - 1e-6 ? (
              <>
                <span className="text-sky-400/80">{formatSessionOffset(viewStart)}</span>
                <span className="text-[var(--tl-whisper)]"> — </span>
                <span className="text-sky-400/80">{formatSessionOffset(viewEnd)}</span>
                <span className="text-[var(--tl-ghost)]"> view</span>
              </>
            ) : (
              <span className="text-[var(--tl-label)]">Full session</span>
            )}
          </span>
          <span className="shrink-0 text-[var(--tl-fainter)]">
            {`${formatSessionOffset(tMin)} \u2192 ${formatSessionOffset(tMax)}`}
          </span>
        </div>
      </div>

      <div
        ref={zoomSurfaceRef}
        className="flex min-h-0 shrink-0 flex-col overflow-hidden rounded-b-lg ring-1 ring-inset ring-[var(--tl-ring)]"
      >
        {/* Ruler + scrub — directly under time strip */}
        <div className="grid shrink-0 grid-cols-[5.25rem_1fr] border-b border-[var(--tl-border-soft)] bg-[var(--tl-ruler-bg)] sm:grid-cols-[7rem_1fr]">
          <div className="flex items-center border-r border-[var(--tl-border-subtle)] px-1.5 py-1" />
          <div
            ref={scrubRef}
            role="slider"
            aria-valuemin={0}
            aria-valuemax={Math.round(duration)}
            aria-valuenow={Math.round(effectivePlayhead - tMin)}
            aria-label="Session time scrubber"
            tabIndex={0}
            className="relative h-7 cursor-crosshair touch-none overflow-hidden px-1 select-none"
            onPointerDown={onTimelineSurfacePointerDown}
            onPointerMove={onScrubPointerMove}
            onPointerUp={onTimelineSurfacePointerUp}
            onPointerCancel={onTimelineSurfacePointerUp}
            onPointerLeave={onRulerPointerLeave}
            onLostPointerCapture={onTimelineSurfacePointerUp}
            onKeyDown={(e) => {
              if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
                e.preventDefault();
                const step = viewSpan * 0.04 * (e.key === 'ArrowLeft' ? -1 : 1);
                let ts = Math.min(tMax, Math.max(tMin, effectivePlayhead + step));
                const w = scrubWidthRef.current;
                const thr = (20 / w) * viewSpan;
                ts = nearestSnapTime(snapTimes, ts, thr) ?? ts;
                setPlayheadMs(ts, { pause: true });
              }
            }}
          >
            <div
              className="pointer-events-none absolute top-1/2 h-px -translate-y-1/2 bg-[var(--tl-grid-line)]"
              style={{ left: TIMELINE_EDGE_INSET_PX, right: TIMELINE_EDGE_INSET_PX }}
            />
            {ticks.map((tick) => (
              <div
                key={tick.t}
                className="pointer-events-none absolute top-0 flex h-full flex-col items-center"
                style={{ ...timelineMarkerLeftStyle(tick.pct), transform: 'translateX(-50%)' }}
              >
                <div className={cn('w-px bg-[var(--tl-tick-line)]', tick.major ? 'h-2' : 'h-1')} />
                {layout !== 'compact' ? (
                  <span className="mt-0.5 font-mono text-[7px] tabular-nums text-[var(--tl-muted)]">{formatSessionOffset(tick.t)}</span>
                ) : null}
              </div>
            ))}
            <div
              className="pointer-events-none absolute top-0 z-40 h-full w-px bg-gradient-to-b from-rose-400 via-rose-500 to-rose-600 shadow-[0_0_10px_rgba(251,113,133,0.65)]"
              style={{ ...timelineMarkerLeftStyle(playheadPct), transform: 'translateX(-50%)' }}
            >
              <div className="absolute -top-0.5 left-1/2 h-1.5 w-2 -translate-x-1/2 rounded-sm bg-rose-500 shadow-sm" />
            </div>
            <div
              data-playhead-handle
              aria-label="Drag playhead"
              tabIndex={-1}
              className="absolute inset-y-0 z-[58] w-4 cursor-ew-resize touch-none"
              style={{ ...timelineMarkerLeftStyle(playheadPct), transform: 'translateX(-50%)' }}
              onPointerDown={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setPlayheadDragging(true);
              }}
            />
          </div>
        </div>

        {/* Track stack — natural height; scroll only if many rows exceed viewport (outer max-h). */}
        <div ref={trackScrollRef} className="min-h-0 shrink-0 overflow-y-auto overflow-x-hidden">
          <div className="flex min-w-0 flex-col">
          {/* Steps */}
          <div
            data-timeline-track="steps"
            className={cn('grid grid-cols-[5.25rem_1fr] border-b border-[var(--tl-row-border)] sm:grid-cols-[7rem_1fr]', rowH)}
          >
            <div className="flex items-center gap-1.5 border-r border-[var(--tl-border-subtle)] bg-[var(--tl-label-col-bg)] px-1.5">
              <MousePointer2 className="h-3 w-3 shrink-0 text-sky-400/70" aria-hidden />
              <span className={cn('font-semibold uppercase tracking-wide text-[var(--tl-muted)]', labelSize)}>Steps</span>
            </div>
            <div
              className="relative min-w-0 touch-none select-none overflow-hidden bg-[var(--tl-track-a)]"
              onPointerDown={onTimelineSurfacePointerDown}
              onPointerMove={onTimelineSurfacePointerMove}
              onPointerUp={onTimelineSurfacePointerUp}
              onPointerCancel={onTimelineSurfacePointerUp}
              onLostPointerCapture={onTimelineSurfacePointerUp}
            >
              <TrackPlayheadLine pct={playheadPct} />
              {steps.map((s, zi) => {
                const tDot = s.timestampStart;
                const leftPct = pctInView(viewStart, viewEnd, tDot);
                const err = s.kind === 'error';
                const nav = s.kind === 'navigate';
                const tone: DotTone = err ? 'error' : nav ? 'violet' : 'success';
                const playheadIn = effectivePlayhead >= s.timestampStart && effectivePlayhead <= s.timestampEnd;
                const isFirstFail = Boolean(firstError?.tab === 'steps' && firstError.id === s.id);
                const dimmed = focusErrorsOnly && !isErrorOrWarnTone(tone);
                const tipLines = err
                  ? [
                      'ERROR',
                      ...(isFirstFail ? ['First failure in this session'] : []),
                      s.title,
                      `Started ${formatSessionOffset(s.timestampStart)} · ended ${formatSessionOffset(s.timestampEnd)}`,
                      ...(s.subtitle ? [s.subtitle] : []),
                    ]
                  : [
                      'Step',
                      s.title,
                      `Started ${formatSessionOffset(s.timestampStart)} · ended ${formatSessionOffset(s.timestampEnd)}`,
                      ...(s.subtitle ? [s.subtitle] : []),
                    ];
                const seekMs = (s.timestampStart + s.timestampEnd) / 2;
                const z =
                  selectedId === s.id ? 48 : playheadIn ? 40 : Math.min(30, 10 + zi);
                return (
                  <TimelineDot
                    key={s.id}
                    leftPct={leftPct}
                    zIndex={z}
                    tone={tone}
                    playheadActive={playheadIn}
                    isSelected={selectedId === s.id}
                    isFirstFailure={isFirstFail}
                    dimmed={dimmed}
                    isCorrelated={false}
                    tipLines={tipLines}
                    ariaLabel={`Step: ${s.title}`}
                    onHoverTip={setHoverTip}
                    onSelect={(e) => {
                      e.stopPropagation();
                      setSelectedId(s.id);
                      setActiveTab('steps');
                      setPlayheadMs(seekMs, { pause: true });
                    }}
                  />
                );
              })}
            </div>
          </div>

          {/* Actions */}
          <div
            data-timeline-track="actions"
            className={cn('grid grid-cols-[5.25rem_1fr] border-b border-[var(--tl-row-border)] sm:grid-cols-[7rem_1fr]', rowH)}
          >
            <div className="flex items-center gap-1.5 border-r border-[var(--tl-border-subtle)] bg-[var(--tl-label-col-bg)] px-1.5">
              <Zap className="h-3 w-3 shrink-0 text-amber-300/70" aria-hidden />
              <span className={cn('font-semibold uppercase tracking-wide text-[var(--tl-muted)]', labelSize)}>Actions</span>
            </div>
            <div
              className="relative min-w-0 touch-none select-none overflow-hidden bg-[var(--tl-track-b)]"
              onPointerDown={onTimelineSurfacePointerDown}
              onPointerMove={onTimelineSurfacePointerMove}
              onPointerUp={onTimelineSurfacePointerUp}
              onPointerCancel={onTimelineSurfacePointerUp}
              onLostPointerCapture={onTimelineSurfacePointerUp}
            >
              <TrackPlayheadLine pct={playheadPct} />
              {actions.map((a, zi) => {
                const leftPct = pctInView(viewStart, viewEnd, a.timestamp);
                const err = a.kind === 'error';
                const nav = a.kind === 'navigate';
                const tone: DotTone = err ? 'error' : nav ? 'violet' : 'neutral';
                const wMs = Math.max(duration * 0.004, 12);
                const playheadIn = effectivePlayhead >= a.timestamp && effectivePlayhead <= a.timestamp + wMs;
                const isFirstFail = Boolean(firstError?.tab === 'actions' && firstError.id === a.id);
                const dimmed = focusErrorsOnly && !isErrorOrWarnTone(tone);
                const isCorr = Boolean(correlation?.prevActionId === a.id);
                const tipLines = err
                  ? [
                      'ERROR',
                      ...(isFirstFail ? ['First failure in this session'] : []),
                      a.summary,
                      formatSessionOffset(a.timestamp),
                      `Kind: ${a.kind}`,
                    ]
                  : ['Action', a.summary, formatSessionOffset(a.timestamp), `Kind: ${a.kind}`];
                const z = selectedId === a.id ? 48 : playheadIn ? 40 : Math.min(30, 10 + zi);
                return (
                  <TimelineDot
                    key={a.id}
                    leftPct={leftPct}
                    zIndex={z}
                    tone={tone}
                    playheadActive={playheadIn}
                    isSelected={selectedId === a.id}
                    isFirstFailure={isFirstFail}
                    dimmed={dimmed}
                    isCorrelated={isCorr}
                    tipLines={tipLines}
                    ariaLabel={`Action: ${a.summary}`}
                    onHoverTip={setHoverTip}
                    onSelect={(e) => {
                      e.stopPropagation();
                      setSelectedId(a.id);
                      setActiveTab('actions');
                      setPlayheadMs(a.timestamp, { pause: true });
                    }}
                  />
                );
              })}
            </div>
          </div>

          {/* Network */}
          <div
            data-timeline-track="network"
            className={cn('grid grid-cols-[5.25rem_1fr] border-b border-[var(--tl-row-border)] sm:grid-cols-[7rem_1fr]', rowH)}
          >
            <div className="flex items-center gap-1.5 border-r border-[var(--tl-border-subtle)] bg-[var(--tl-label-col-bg)] px-1.5">
              <Globe className="h-3 w-3 shrink-0 text-emerald-400/70" aria-hidden />
              <span className={cn('font-semibold uppercase tracking-wide text-[var(--tl-muted)]', labelSize)}>Network</span>
            </div>
            <div
              className="relative min-w-0 touch-none select-none overflow-hidden bg-[var(--tl-track-a)]"
              onPointerDown={onTimelineSurfacePointerDown}
              onPointerMove={onTimelineSurfacePointerMove}
              onPointerUp={onTimelineSurfacePointerUp}
              onPointerCancel={onTimelineSurfacePointerUp}
              onLostPointerCapture={onTimelineSurfacePointerUp}
            >
              <TrackPlayheadLine pct={playheadPct} />
              {filteredNetworkLaneRows.map((row, zi) => {
                if (row.kind === 'network') {
                  const n = row.entry;
                  const end = n.timestamp + Math.max(n.timing.durationMs, 2);
                  const tStart = n.timestamp;
                  const leftPct = pctInView(viewStart, viewEnd, tStart);
                  const st = n.response?.status ?? 0;
                  const bad = isNetworkError(st, n.error);
                  const warn = !bad && st >= 400 && st < 500;
                  const tone: DotTone = bad ? 'error' : warn ? 'warn' : 'success';
                  const playheadIn = effectivePlayhead >= n.timestamp && effectivePlayhead <= end;
                  const isFirstFail = Boolean(firstError?.tab === 'network' && firstError.id === n.id);
                  const dimmed = focusErrorsOnly && !isErrorOrWarnTone(tone);
                  const isCorr = Boolean(correlation?.relatedNetworkId === n.id);
                  const tipLines = bad
                    ? [
                        'ERROR',
                        ...(isFirstFail ? ['First failure in this session'] : []),
                        `${n.request.method} ${n.request.url}`,
                        `Status: ${st || '—'} · ${formatDuration(n.timing.durationMs)}`,
                        ...(n.error?.message ? [n.error.message] : []),
                        `Started ${formatSessionOffset(tStart)} · ended ${formatSessionOffset(end)}`,
                      ]
                    : warn
                      ? [
                          'WARNING',
                          `${n.request.method} ${n.request.url}`,
                          `Status: ${st} · ${formatDuration(n.timing.durationMs)}`,
                          `Started ${formatSessionOffset(tStart)} · ended ${formatSessionOffset(end)}`,
                        ]
                      : [
                          'Network (page / XHR)',
                          `${n.request.method} ${n.request.url}`,
                          `Status: ${st || '—'} · ${formatDuration(n.timing.durationMs)}`,
                          `Started ${formatSessionOffset(tStart)} · ended ${formatSessionOffset(end)}`,
                        ];
                  const seekMs = (tStart + end) / 2;
                  const z = selectedId === n.id ? 48 : playheadIn ? 40 : Math.min(30, 10 + zi);
                  return (
                    <TimelineDot
                      key={n.id}
                      leftPct={leftPct}
                      zIndex={z}
                      tone={tone}
                      playheadActive={playheadIn}
                      isSelected={selectedId === n.id}
                      isFirstFailure={isFirstFail}
                      dimmed={dimmed}
                      isCorrelated={isCorr}
                      tipLines={tipLines}
                      ariaLabel={`Network: ${n.request.method} ${shortUrl(n.request.url, 40)}`}
                      onHoverTip={setHoverTip}
                      onSelect={(e) => {
                        e.stopPropagation();
                        setSelectedId(n.id);
                        setActiveTab('network');
                        setPlayheadMs(seekMs, { pause: true });
                      }}
                    />
                  );
                }
                const r = row.entry;
                const end = r.timestamp + Math.max(r.timing.durationMs, 2);
                const tStart = r.timestamp;
                const leftPct = pctInView(viewStart, viewEnd, tStart);
                const bad = Boolean(r.error) || r.status >= 400;
                const warn = !bad && r.status >= 300 && r.status < 400;
                const tone: DotTone = bad ? 'error' : warn ? 'warn' : 'neutral';
                const playheadIn = effectivePlayhead >= r.timestamp && effectivePlayhead <= end;
                const isFirstFail = Boolean(firstError?.tab === 'network' && firstError.id === r.id);
                const dimmed = focusErrorsOnly && !isErrorOrWarnTone(tone);
                const isCorr = Boolean(correlation?.relatedNetworkId === r.id);
                const errMsg = typeof r.error === 'string' ? r.error : '';
                const tipLines = bad
                  ? [
                      'ERROR',
                      ...(isFirstFail ? ['First failure in this session'] : []),
                      `Resource (${r.resourceType})`,
                      r.url,
                      `Status: ${r.status} · ${formatDuration(r.timing.durationMs)}`,
                      ...(errMsg ? [errMsg] : []),
                      `Started ${formatSessionOffset(tStart)} · ended ${formatSessionOffset(end)}`,
                    ]
                  : warn
                    ? [
                        'WARNING',
                        `${r.resourceType}`,
                        r.url,
                        `Status: ${r.status} · ${formatDuration(r.timing.durationMs)}`,
                        `Started ${formatSessionOffset(tStart)} · ended ${formatSessionOffset(end)}`,
                      ]
                    : [
                        `Resource (${r.resourceType})`,
                        r.url,
                        `Status: ${r.status} · ${formatDuration(r.timing.durationMs)}`,
                        `Started ${formatSessionOffset(tStart)} · ended ${formatSessionOffset(end)}`,
                      ];
                const seekMs = (tStart + end) / 2;
                const z = selectedId === r.id ? 48 : playheadIn ? 40 : Math.min(30, 10 + zi);
                return (
                  <TimelineDot
                    key={r.id}
                    leftPct={leftPct}
                    zIndex={z}
                    tone={tone}
                    playheadActive={playheadIn}
                    isSelected={selectedId === r.id}
                    isFirstFailure={isFirstFail}
                    dimmed={dimmed}
                    isCorrelated={isCorr}
                    tipLines={tipLines}
                    ariaLabel={`Resource: ${r.resourceType} ${shortUrl(r.url, 40)}`}
                    onHoverTip={setHoverTip}
                    onSelect={(e) => {
                      e.stopPropagation();
                      setSelectedId(r.id);
                      setActiveTab('network');
                      setPlayheadMs(seekMs, { pause: true });
                    }}
                  />
                );
              })}
            </div>
          </div>

          {/* Events / console */}
          <div
            data-timeline-track="events"
            className={cn('grid grid-cols-[5.25rem_1fr] border-b border-[var(--tl-row-border)] sm:grid-cols-[7rem_1fr]', rowH)}
          >
            <div className="flex items-center gap-1.5 border-r border-[var(--tl-border-subtle)] bg-[var(--tl-label-col-bg)] px-1.5">
              <Terminal className="h-3 w-3 shrink-0 text-violet-300/70" aria-hidden />
              <span className={cn('font-semibold uppercase tracking-wide text-[var(--tl-muted)]', labelSize)}>Events</span>
            </div>
            <div
              className="relative min-w-0 touch-none select-none overflow-hidden bg-[var(--tl-track-b)]"
              onPointerDown={onTimelineSurfacePointerDown}
              onPointerMove={onTimelineSurfacePointerMove}
              onPointerUp={onTimelineSurfacePointerUp}
              onPointerCancel={onTimelineSurfacePointerUp}
              onLostPointerCapture={onTimelineSurfacePointerUp}
            >
              <TrackPlayheadLine pct={playheadPct} />
              {events.map((ev, zi) => {
                const leftPct = pctInView(viewStart, viewEnd, ev.timestamp);
                const err = isEventError(ev);
                const nav = ev.type.startsWith('navigation.');
                const tone: DotTone = err ? 'error' : nav ? 'violet' : 'neutral';
                const wMs = Math.max(duration * 0.0035, 10);
                const playheadIn = effectivePlayhead >= ev.timestamp && effectivePlayhead <= ev.timestamp + wMs;
                const isFirstFail = Boolean(firstError?.tab === 'events' && firstError.id === ev.id);
                const dimmed = focusErrorsOnly && !isErrorOrWarnTone(tone);
                const payload = ev.payload as Record<string, unknown> | null | undefined;
                const payloadMsg =
                  (payload && typeof payload.message === 'string' && payload.message) ||
                  (payload && typeof payload.error === 'string' && payload.error) ||
                  '';
                const tipLines = err
                  ? [
                      'ERROR',
                      ...(isFirstFail ? ['First failure in this session'] : []),
                      ev.type,
                      formatSessionOffset(ev.timestamp),
                      ...(ev.selector ? [`Selector: ${ev.selector}`] : []),
                      ...(payloadMsg ? [String(payloadMsg)] : []),
                    ]
                  : [
                      'Event',
                      ev.type,
                      formatSessionOffset(ev.timestamp),
                      ...(ev.selector ? [`Selector: ${ev.selector}`] : []),
                    ];
                const z = selectedId === ev.id ? 48 : playheadIn ? 40 : Math.min(30, 10 + zi);
                return (
                  <TimelineDot
                    key={ev.id}
                    leftPct={leftPct}
                    zIndex={z}
                    tone={tone}
                    playheadActive={playheadIn}
                    isSelected={selectedId === ev.id}
                    isFirstFailure={isFirstFail}
                    dimmed={dimmed}
                    isCorrelated={false}
                    tipLines={tipLines}
                    ariaLabel={`Event: ${ev.type}`}
                    onHoverTip={setHoverTip}
                    onSelect={(e) => {
                      e.stopPropagation();
                      setSelectedId(ev.id);
                      setActiveTab('events');
                      setPlayheadMs(ev.timestamp, { pause: true });
                    }}
                  />
                );
              })}
            </div>
          </div>
          </div>
        </div>

        {/* Transport dock — single compact row, wraps on narrow widths */}
        <div className="shrink-0 border-t border-[var(--tl-dock-border)] bg-gradient-to-b from-[var(--tl-dock-from)] to-[var(--tl-dock-to)] px-1.5 py-1 shadow-[var(--tl-dock-shadow)] sm:px-2 sm:py-1.5">
          <div className="mx-auto flex w-full max-w-3xl flex-wrap items-center justify-center gap-x-1 gap-y-1 sm:gap-x-1.5">
            <button
              type="button"
              aria-label="Jump to session start"
              title="Jump to session start"
              onClick={() => setPlayheadMs(tMin, { pause: true })}
              className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md border border-[var(--tl-control-border)] bg-[var(--tl-control-bg)] text-[var(--tl-control-text)] transition hover:border-sky-500/30 hover:bg-sky-500/10 hover:text-sky-100"
            >
              <ChevronsLeft className="h-3.5 w-3.5" />
            </button>
            <button
              type="button"
              aria-label="Reset playback"
              title="Reset playback to start"
              onClick={() => resetPlayback()}
              className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md border border-[var(--tl-control-border)] bg-[var(--tl-control-bg)] text-[var(--tl-control-text)] transition hover:bg-[var(--tl-control-hover-bg)] hover:text-[var(--tl-control-hover-text)]"
            >
              <RotateCcw className="h-3 w-3" />
            </button>
            <button
              type="button"
              aria-label="Step back"
              onClick={() => stepPlaybackLandmarks(landmarks, -1)}
              className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md border border-[var(--tl-control-border)] bg-[var(--tl-control-bg-2)] text-[var(--tl-control-text-mid)] transition hover:bg-[var(--tl-control-hover-bg)] hover:text-[var(--tl-control-hover-text)]"
            >
              <SkipBack className="h-3.5 w-3.5" />
            </button>
            <button
              type="button"
              aria-label={isPlaying ? 'Pause' : 'Play'}
              onClick={() => togglePlayback()}
              className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-sky-400/45 bg-gradient-to-b from-sky-400 to-sky-600 text-[var(--on-accent)] shadow-md shadow-sky-500/20 transition hover:brightness-110 active:scale-[0.97]"
            >
              {isPlaying ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4 pl-0.5" />}
            </button>
            <button
              type="button"
              aria-label="Step forward"
              onClick={() => stepPlaybackLandmarks(landmarks, 1)}
              className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md border border-[var(--tl-control-border)] bg-[var(--tl-control-bg-2)] text-[var(--tl-control-text-mid)] transition hover:bg-[var(--tl-control-hover-bg)] hover:text-[var(--tl-control-hover-text)]"
            >
              <SkipForward className="h-3.5 w-3.5" />
            </button>
            <button
              type="button"
              aria-label="Jump to session end"
              title="Jump to session end"
              onClick={() => setPlayheadMs(tMax, { pause: true })}
              className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md border border-[var(--tl-control-border)] bg-[var(--tl-control-bg)] text-[var(--tl-control-text)] transition hover:border-violet-400/30 hover:bg-violet-500/10 hover:text-violet-100"
            >
              <ChevronsRight className="h-3.5 w-3.5" />
            </button>
            <div className="mx-0.5 hidden h-5 w-px bg-[var(--tl-divider-v)] sm:block" aria-hidden />
            <label className="flex cursor-pointer items-center gap-1 rounded-md border border-[var(--tl-pill-border)] bg-[var(--tl-pill-bg)] px-1.5 py-0.5">
              <span className="text-[8px] font-medium uppercase tracking-wide text-[var(--tl-muted-2)]">Speed</span>
              <select
                aria-label="Playback speed"
                value={playbackRate}
                onChange={(e) => setPlaybackRate(Number(e.target.value))}
                className="h-6 max-w-[4.5rem] cursor-pointer rounded border-0 bg-transparent py-0 pr-5 text-[10px] font-semibold tabular-nums text-[var(--tl-speed-select)] outline-none focus:ring-0"
              >
                <option value={0.5}>0.5×</option>
                <option value={1}>1×</option>
                <option value={2}>2×</option>
                <option value={4}>4×</option>
              </select>
            </label>
            <div className="mx-0.5 hidden h-5 w-px bg-[var(--tl-divider-v)] md:block" aria-hidden />
            <label className="flex cursor-pointer items-center gap-1 rounded-md px-1 py-0.5 hover:bg-[var(--tl-hover-ui)]">
              <input
                type="checkbox"
                checked={focusErrorsOnly}
                onChange={(e) => setFocusErrorsOnly(e.target.checked)}
                className="h-2.5 w-2.5 shrink-0 rounded border-[var(--tl-checkbox-border)] bg-[var(--tl-checkbox-bg)] accent-red-500"
              />
              <span className="text-[8px] font-medium uppercase tracking-wide text-[var(--tl-muted)]">Errors</span>
            </label>
            <div className="flex items-center gap-px rounded-md border border-[var(--tl-pill-border)] bg-[var(--tl-pill-bg)] p-px">
              <button
                type="button"
                aria-label="Zoom timeline in"
                onClick={() => zoomInAt(anchorForZoomUi())}
                className="flex h-6 w-6 items-center justify-center rounded text-[var(--tl-control-text)] transition hover:bg-[var(--tl-control-hover-bg)] hover:text-[var(--tl-control-hover-text)]"
              >
                <ZoomIn className="h-3 w-3" />
              </button>
              <button
                type="button"
                aria-label="Zoom timeline out"
                onClick={() => zoomOutAt(anchorForZoomUi())}
                className="flex h-6 w-6 items-center justify-center rounded text-[var(--tl-control-text)] transition hover:bg-[var(--tl-control-hover-bg)] hover:text-[var(--tl-control-hover-text)]"
              >
                <ZoomOut className="h-3 w-3" />
              </button>
              <button
                type="button"
                aria-label="Fit entire session in view"
                title={
                  layout === 'compact'
                    ? 'Fit full session. Zoom: ⌘/Ctrl+wheel.'
                    : 'Fit full session. Zoom: ⌘/Ctrl+wheel, pan: two-finger or Shift+wheel.'
                }
                onClick={() => setViewWindow({ start: tMin, end: tMax })}
                className="flex h-6 w-6 items-center justify-center rounded text-emerald-300/85 transition hover:bg-emerald-500/15 hover:text-emerald-200"
              >
                <Expand className="h-3 w-3" />
              </button>
            </div>
            <label className="flex cursor-pointer items-center gap-1 rounded-md px-1 py-0.5 hover:bg-[var(--tl-hover-ui)]">
              <input
                type="checkbox"
                checked={followPlayhead}
                onChange={(e) => setFollowPlayhead(e.target.checked)}
                className="h-2.5 w-2.5 shrink-0 rounded border-[var(--tl-checkbox-border)] bg-[var(--tl-checkbox-bg)] accent-sky-500"
              />
              <Crosshair className="h-2.5 w-2.5 text-sky-400/80" aria-hidden />
              <span className="text-[8px] font-medium uppercase tracking-wide text-[var(--tl-muted)]">Follow</span>
            </label>
          </div>
        </div>
      </div>
    </div>
  );
};
